/**
 * TagsCell Component
 *
 * Displays and edits tags inline in a transaction table cell.
 * Shows tags as compact badges with click-to-edit functionality.
 */

import { useState } from 'react';
import { TagBadge } from '@/components/accounting/tags/TagBadge';
import { TransactionTagEditor } from '@/components/accounting/tags/TransactionTagEditor';
import type { Tag, Tagging, TaggingSource, TagType } from '@/types/accountingTags';
import type { Transaction } from '@/types/accounting';
import type ApiAccountingTransactionTag from '@/types/serializers/ApiAccountingTransactionTag';

export interface TagsCellProps {
  /** The transaction containing tags */
  transaction: Transaction;
  /** All available tags for the dropdown */
  availableTags: Tag[];
  /** Whether editing is enabled */
  editable?: boolean;
  /** Callback when a tag is added */
  onAddTag?: (transactionId: number, tag: Tag) => Promise<void>;
  /** Callback when a tag is removed */
  onRemoveTag?: (transactionId: number, tagging: { tag: { tagType: string; value: string } }) => Promise<void>;
}

/**
 * Convert ApiAccountingTransactionTag to Tagging format for TransactionTagEditor
 */
function apiTagToTagging(
  apiTag: ApiAccountingTransactionTag,
  transactionId: number
): Tagging {
  return {
    id: apiTag.taggingId,
    tagId: apiTag.id,
    transactionId,
    source: apiTag.source as TaggingSource,
    confidence: apiTag.confidence,
    tag: {
      id: apiTag.id,
      tagType: apiTag.tagType as TagType,
      value: apiTag.value,
      displayName: apiTag.displayName ?? null,
      color: apiTag.color ?? null,
    },
  };
}

/**
 * TagsCell displays tags in a table cell with inline editing capability.
 */
export function TagsCell({
  transaction,
  availableTags,
  editable = true,
  onAddTag,
  onRemoveTag,
}: TagsCellProps) {
  const [isEditing, setIsEditing] = useState(false);

  // Get tags from transaction, defaulting to empty array if undefined
  const tags = transaction.tags ?? [];

  // Convert API tags to Tagging format
  const taggings = tags.map((tag) => apiTagToTagging(tag, transaction.id));

  const handleCellClick = (e: React.MouseEvent) => {
    if (editable) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleAddTag = (tag: Tag) => {
    onAddTag?.(transaction.id, tag);
  };

  const handleRemoveTag = (tagging: Tagging) => {
    onRemoveTag?.(transaction.id, {
      tag: {
        tagType: tagging.tag.tagType,
        value: tagging.tag.value,
      },
    });
  };

  // Display mode: show compact badges
  if (!isEditing) {
    return (
      <div
        className="flex flex-wrap gap-1 cursor-pointer min-h-[24px] items-center"
        onClick={handleCellClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (editable) {
              setIsEditing(true);
            }
          }
        }}
        data-testid="tags-cell"
      >
        {tags.length === 0 ? (
          <span className="text-xs text-gray-400 italic">No tags</span>
        ) : (
          tags.map((tag) => (
            <TagBadge
              key={tag.taggingId}
              tag={{
                id: tag.id,
                tagType: tag.tagType as TagType,
                value: tag.value,
                displayName: tag.displayName ?? null,
                color: tag.color ?? null,
              }}
              size="sm"
              source={tag.source as TaggingSource}
            />
          ))
        )}
      </div>
    );
  }

  // Edit mode: show full editor with dropdown auto-opened
  return (
    <div onClick={(e) => e.stopPropagation()} data-testid="tags-cell-editor">
      <TransactionTagEditor
        taggings={taggings}
        availableTags={availableTags}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        compact
        autoOpen
      />
    </div>
  );
}

export default TagsCell;
